import { useMemo } from "react";
import { useGetList } from "react-admin";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { Policy } from "@/types";
import { PolicyMonthlyEnrollment } from "@/types/policyMonthlyEnrollment";
import { getEnrollmentDueMonth, getLatestRecordedSummary } from "../utils/enrollmentStatus";

/**
 * Enrollment status for a policy's Current Policy card (US 5045 / ADR-047).
 * Three states:
 *   1. A completed coverage month is missing  -> amber "Enrollment due" chip.
 *   2. Up to date with data                   -> latest recorded month + delta.
 *   3. Nothing recorded yet (and nothing due) -> muted hint.
 * Pass onClick to make it navigable (e.g. to the policy).
 */
export const PolicyEnrollmentStatus = ({ policy, onClick }: { policy: Policy; onClick?: () => void }) => {
  const policyId = policy?.id ? String(policy.id) : undefined;

  const { data = [], isLoading } = useGetList<PolicyMonthlyEnrollment>(
    `policies/${policyId}/monthly-enrollments`,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "coverage_month", order: "ASC" },
    },
    { enabled: !!policyId }
  );

  const { due, summary } = useMemo(() => {
    if (isLoading) return { due: null, summary: null };
    const recorded = new Set(data.map((e) => e.coverage_month));
    return {
      due: getEnrollmentDueMonth(policy.effective_start_date, policy.effective_end_date, recorded, new Date()),
      summary: getLatestRecordedSummary(data),
    };
  }, [isLoading, data, policy.effective_start_date, policy.effective_end_date]);

  if (isLoading) return null;

  const clickSx = onClick ? { cursor: "pointer" } : {};

  // 1) Action needed: latest completed month has no enrollment.
  if (due) {
    return (
      <Tooltip title={`No enrollment recorded for ${due.label}`}>
        <Box component="span" onClick={onClick} sx={{ display: "inline-flex", mb: 2, ...clickSx }}>
          <StatusChip label={`Enrollment due: ${due.label}`} color={ColorType.Warning} size={UIComponentSize.Small} />
        </Box>
      </Tooltip>
    );
  }

  // 2) Up to date with data: latest recorded month + delta vs prior recorded month.
  if (summary) {
    const hasDelta = summary.delta !== null && summary.delta !== 0;
    const up = (summary.delta ?? 0) > 0;
    return (
      <Box onClick={onClick} sx={{ bgcolor: "action.hover", borderRadius: 1, px: 2, py: 1.5, mb: 2, ...clickSx }}>
        <Typography variant="caption" color="text.secondary">
          Latest Enrollment
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={500}>
            {summary.lives.toLocaleString()} lives · {summary.label}
          </Typography>
          {hasDelta && (
            <Typography variant="caption" sx={{ color: up ? "success.main" : "error.main", fontWeight: 600 }}>
              {up ? "▲" : "▼"} {Math.abs(summary.delta as number)} vs prior
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  // 3) Nothing recorded yet and nothing due (e.g. policy not yet into a completed month).
  return (
    <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
      No enrollment recorded yet
    </Typography>
  );
};
